import { FeatureFlag } from '@/constants/feature-flags.ts';
import { type AppointmentChainConfig } from '@/models/appointment-chain-config.ts';
import { useTranslation } from 'react-i18next';

import { useChainConfigs } from '@/hooks/use-chain-configs.ts';
import { useFeatureFlag } from '@/hooks/use-feature-flag.ts';

import {
    ACCORDION_VIEW_APPOINTMENT_SELECTION_LIMIT,
    CHAINS_CATEGORY_KEY,
    MIN_SEARCH_LENGTH,
} from '@/utils/constants.js';
import { hasCategoryViewFlags } from '@/utils/customer-view-utils.ts';
import { getText } from '@/utils/helpers.js';
import { formatInsuranceType, isTreatmentAvailableForInsurance } from '@/utils/insurance-utils.ts';

import {
    CLEARED_CHAIN_SELECTION,
    selectChainConfig,
    selectSingleTreatment,
    useConfigStore,
    useSearchStore,
} from '@/store.js';

export interface CategoryBrowserTreatment {
    rowKey: string;
    treatmentKey: string;
    name: string;
    isOnline: boolean;
    isSelected: boolean;
    /** Owns the treatment-vs-chain branch, so the cards stay presentational. */
    select: () => void;
    meta?: string;
}

export interface CategoryBrowserCategory {
    key: string;
    name: string;
    treatments: CategoryBrowserTreatment[];
}

interface CategoryBrowserData {
    categories: CategoryBrowserCategory[];
    isSearchActive: boolean;
    selectedLocation: string | null;
    showCategoryBrowser: boolean;
}

const formatSearch = (search: string | null | undefined): string => (search ?? '').toLowerCase().trim();

interface TreatmentRowContext {
    selectedTreatments: Record<string, boolean> | undefined;
    isOnlineAppointment: boolean;
    selectTreatment: (treatmentKey: string, isOnline: boolean) => void;
}

const buildTreatmentRows = (
    categoryKey: string,
    treatment: Record<string, unknown>,
    context: TreatmentRowContext
): CategoryBrowserTreatment[] => {
    const treatmentKey = getText(treatment.key) as string;
    const name = getText(treatment.name) as string;
    const meta = [getText(treatment.duration), getText(treatment.price)]
        .filter((part): part is string => Boolean(part))
        .join(' · ');
    const makeRow = (isOnline: boolean): CategoryBrowserTreatment => ({
        rowKey: `${categoryKey}:${treatmentKey}:${isOnline ? 'online' : 'location'}`,
        treatmentKey: treatmentKey,
        name: name,
        isOnline: isOnline,
        isSelected: Boolean(context.selectedTreatments?.[treatmentKey]) && isOnline === context.isOnlineAppointment,
        select: () => context.selectTreatment(treatmentKey, isOnline),
        meta: meta.length > 0 ? meta : undefined,
    });

    return [...(treatment.location ? [makeRow(false)] : []), ...(treatment.online ? [makeRow(true)] : [])];
};

const toggleStagedTreatment = (treatmentKey: string, isOnline: boolean): void => {
    const { selectedTreatments, isOnlineAppointment } = useSearchStore.getState();
    const newSelectedTreatments: Record<string, boolean> = { ...selectedTreatments };

    if (newSelectedTreatments[treatmentKey] && isOnline === isOnlineAppointment) {
        delete newSelectedTreatments[treatmentKey];
    } else if (
        Object.keys(newSelectedTreatments).length === 0 ||
        (!isOnlineAppointment &&
            !isOnline &&
            Object.keys(newSelectedTreatments).length < ACCORDION_VIEW_APPOINTMENT_SELECTION_LIMIT)
    ) {
        newSelectedTreatments[treatmentKey] = true;
    } else {
        return;
    }

    useSearchStore.setState({
        selectedTreatments: newSelectedTreatments,
        ...CLEARED_CHAIN_SELECTION,
        isOnlineAppointment: isOnline,
    });
};

export const useCategoryBrowser = (): CategoryBrowserData => {
    const currentCustomer = useConfigStore((state: any) => state.currentCustomer);
    const treatments = useSearchStore((state: any) => state.treatments);
    const selectedLocation = useSearchStore((state: any) => state.selectedLocation);
    const selectedHealthInsurance = useSearchStore((state: any) => state.selectedHealthInsurance);
    const treatmentTypeCategories = useSearchStore((state: any) => state.treatmentTypeCategories);
    const search = useSearchStore((state: any) => state.search);
    const selectedTreatments = useSearchStore((state: any) => state.selectedTreatments);
    const isOnlineAppointment = useSearchStore((state: any) => state.isOnlineAppointment);

    const isMultiSelect = Boolean(currentCustomer?.is_multi_treatment_booking_allowed);
    const selectTreatment = isMultiSelect ? toggleStagedTreatment : selectSingleTreatment;

    const formattedSearch = formatSearch(search as string | null);
    const isSearchActive = formattedSearch.length >= MIN_SEARCH_LENGTH;

    const rowContext: TreatmentRowContext = {
        selectedTreatments: selectedTreatments,
        isOnlineAppointment: isOnlineAppointment,
        selectTreatment: selectTreatment,
    };

    const matchesSearch = (treatment: Record<string, unknown>): boolean => {
        if (!isSearchActive) return true;
        if ((getText(treatment.name) ?? '').toLowerCase().includes(formattedSearch)) return true;
        const searchTags = (getText(treatment.search_tags) ?? '') as string;
        return searchTags
            .toLowerCase()
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean)
            .some((tag) => tag.includes(formattedSearch));
    };

    const customerCategories = (currentCustomer?.categories ?? {}) as Record<string, Record<string, unknown>>;
    const locationTreatments = (treatments?.[selectedLocation] ?? {}) as Record<
        string,
        Record<string, unknown> | undefined
    >;

    let hasBrowsableTreatments = false;
    const categories = Object.keys(customerCategories)
        .map((categoryKey) => {
            const availableTreatments = Object.keys(customerCategories[categoryKey] ?? {})
                .map((treatmentKey) => locationTreatments[treatmentKey])
                .filter((treatment): treatment is Record<string, unknown> => Boolean(treatment))
                .filter((treatment) => Boolean(getText(treatment.name)))
                .filter((treatment) => Boolean(getText(treatment.key)))
                .filter((treatment) => Boolean(treatment.location) || Boolean(treatment.online))
                .filter((treatment) =>
                    isTreatmentAvailableForInsurance(
                        treatment as { public_insurance?: boolean; private_insurance?: boolean; self_payer?: boolean },
                        selectedHealthInsurance as string | null | undefined
                    )
                );
            hasBrowsableTreatments = hasBrowsableTreatments || availableTreatments.length > 0;

            const categoryTreatments = availableTreatments
                .filter((treatment) => matchesSearch(treatment))
                .flatMap((treatment) => buildTreatmentRows(categoryKey, treatment, rowContext));

            const categoryDoc = treatmentTypeCategories?.[categoryKey] as Record<string, unknown> | undefined;
            const categoryName = getText(categoryDoc?.name) as string | undefined;

            return {
                key: categoryKey,
                name: categoryName ?? categoryKey,
                treatments: categoryTreatments,
            };
        })
        .filter((category) => category.treatments.length > 0);

    return {
        categories: categories,
        isSearchActive: isSearchActive,
        selectedLocation: (selectedLocation as string | null) ?? null,
        showCategoryBrowser: hasCategoryViewFlags(currentCustomer) && hasBrowsableTreatments,
    };
};

export const useBrowsableCategories = (): CategoryBrowserData => {
    const base = useCategoryBrowser();
    const currentCustomer = useConfigStore((state: any) => state.currentCustomer);
    const selectedLocation = useSearchStore((state: any) => state.selectedLocation);
    const selectedHealthInsurance = useSearchStore((state: any) => state.selectedHealthInsurance);
    const selectedChainConfig = useSearchStore((state: any) => state.selectedChainConfig);
    const search = useSearchStore((state: any) => state.search);
    const { t, i18n } = useTranslation();

    // Same arguments as the list view's call, so SWR dedupes against it rather than refetching.
    const insuranceType = formatInsuranceType(selectedHealthInsurance);
    const { value: isChainBookingEnabled } = useFeatureFlag(FeatureFlag.IS_CHAIN_BOOKING_ENABLED_FOR_PATIENTS);
    const { chainConfigs } = useChainConfigs(
        Boolean(isChainBookingEnabled && insuranceType),
        currentCustomer?.customer_id?.[0]?.text,
        selectedLocation ?? currentCustomer?.locations?.[0]?.key?.[0]?.text,
        insuranceType
    );

    const formattedSearch = formatSearch(search as string | null);

    // The payload carries no treatment key, so a chain cannot be placed in a real category.
    // Until the API resolves one, they browse as a category of their own.
    const chainRows = (isChainBookingEnabled ? chainConfigs : [])
        .map((config: AppointmentChainConfig): CategoryBrowserTreatment => {
            const name = (config.name as unknown as Record<string, string>)[i18n.language] ?? config.name.de;
            return {
                rowKey: `${CHAINS_CATEGORY_KEY}:${config.id}`,
                treatmentKey: config.id,
                name: name,
                isOnline: false,
                isSelected: selectedChainConfig?.id === config.id,
                select: () => selectChainConfig(config),
                meta: t('ChainStepCountBadge', { count: config.steps.length }),
            };
        })
        .filter((row) => !base.isSearchActive || row.name.toLowerCase().includes(formattedSearch));

    if (chainRows.length === 0) return base;

    return {
        ...base,
        categories: [
            ...base.categories,
            { key: CHAINS_CATEGORY_KEY, name: t('ChainsCategoryName'), treatments: chainRows },
        ],
    };
};
